// Design tokens and display helpers shared by the web dashboard and the Expo app.
import type { BookingStatus, HealthStatus, InvoiceStatus, Mood } from '@walkkit/shared';

export const colors = {
  brand: '#2F6B4F',
  brandDark: '#1F4A37',
  brandTint: '#E7F1EC',
  ink: '#16211C',
  inkMuted: '#5B6B63',
  line: '#D9E2DD',
  surface: '#FFFFFF',
  canvas: '#F5F7F4',
  ok: '#1E7B4A',
  okTint: '#DDF3E6',
  warn: '#A15C00',
  warnTint: '#FFF0D6',
  danger: '#B42318',
  dangerTint: '#FDE4E1',
  info: '#1D5FA8',
  infoTint: '#E1EDFA',
  // High-contrast mode overrides
  hcInk: '#000000',
  hcSurface: '#FFFFFF',
} as const;

export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;
export const radius = { sm: 6, md: 10, lg: 16, pill: 999 } as const;
/** Glove-and-lead tap targets: never smaller than this. */
export const MIN_TAP = 48;

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export const toneColors: Record<Tone, { fg: string; bg: string }> = {
  ok: { fg: colors.ok, bg: colors.okTint },
  warn: { fg: colors.warn, bg: colors.warnTint },
  danger: { fg: colors.danger, bg: colors.dangerTint },
  info: { fg: colors.info, bg: colors.infoTint },
  neutral: { fg: colors.inkMuted, bg: colors.canvas },
};

export const healthTone: Record<HealthStatus, Tone> = { ok: 'ok', due_30: 'info', due_7: 'warn', expired: 'danger', unknown: 'neutral' };
export const healthLabel: Record<HealthStatus, string> = { ok: 'Up to date', due_30: 'Due in 30 days', due_7: 'Due this week', expired: 'Expired', unknown: 'Unknown' };

export const bookingLabel: Record<BookingStatus, string> = {
  planned: 'Planned',
  done: 'Done',
  skipped_holiday: 'Holiday',
  cancelled_client: 'Cancelled (client)',
  cancelled_business: 'Cancelled (us)',
  no_access: 'No access',
};

export const invoiceTone: Record<InvoiceStatus, Tone> = {
  draft: 'neutral',
  issued: 'info',
  submitted: 'warn',
  paid: 'ok',
  failed: 'danger',
  cancelled: 'neutral',
};
export const invoiceLabel: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  submitted: 'Pending (Bacs)',
  paid: 'Paid',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const moodEmoji: Record<Mood, string> = { happy: '😄', calm: '😌', energetic: '⚡️', nervous: '😟', tired: '😴' };
export const moodLabel: Record<Mood, string> = { happy: 'Happy', calm: 'Calm', energetic: 'Energetic', nervous: 'Nervous', tired: 'Tired' };

export const activityLabel = {
  wee: 'Wee',
  poo: 'Poo',
  drank: 'Drank',
  ate: 'Ate',
  played: 'Played',
  training: 'Training',
  meds_given: 'Meds given',
} as const;

// ---- Added for the web dashboard (additive; safe for mobile) ----

export const bookingTone: Record<BookingStatus, Tone> = {
  planned: 'info',
  done: 'ok',
  skipped_holiday: 'neutral',
  cancelled_client: 'warn',
  cancelled_business: 'neutral',
  no_access: 'danger',
};

/** Compliance tile traffic-light level → tone. */
export const levelTone: Record<'green' | 'amber' | 'red', Tone> = { green: 'ok', amber: 'warn', red: 'danger' };

/** Relative luminance of a #RRGGBB colour (WCAG 2.x). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 0;
  const n = parseInt(m[1]!, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

/** Text colour (ink or white) with the better contrast on a brand background. */
export function readableOn(hex: string): string {
  const l = luminance(hex);
  const onWhite = 1.05 / (l + 0.05);
  const onInk = (l + 0.05) / (luminance(colors.ink) + 0.05);
  return onWhite >= onInk ? '#FFFFFF' : colors.ink;
}
